import { useState, type FormEvent } from 'react';
import { useInviteMember } from '@fluxter/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface InviteMemberModalProps {
  workspaceSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When true, the role select includes `admin` — gated to owners
   *  per slice-8 policy. */
  canInviteAdmin: boolean;
}

/**
 * Modal launched from the People page's "Invite member" button.
 * Resets local state on close so the next open starts fresh.
 *
 * Surface error mapping:
 *  - 409 already_a_member / already_pending → friendly inline error
 *  - 422 invalid email → field-level error
 *  - 403 admin invite by non-owner → caller wouldn't show the role
 *    option, so this branch is mostly belt-and-suspenders
 */
export const InviteMemberModal = ({
  workspaceSlug,
  open,
  onOpenChange,
  canInviteAdmin,
}: InviteMemberModalProps) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [error, setError] = useState<string | null>(null);
  const invite = useInviteMember(workspaceSlug);

  const reset = (): void => {
    setEmail('');
    setRole('member');
    setError(null);
  };

  const handleClose = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setError('Enter an email address.');
      return;
    }
    setError(null);
    try {
      await invite.mutateAsync({ email: trimmed, role });
      handleClose(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send invitation.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
          <DialogDescription>
            They'll get an email with a link to accept. The invitation expires after 7 days.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              autoFocus
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as 'admin' | 'member')}>
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                {canInviteAdmin ? <SelectItem value="admin">Admin</SelectItem> : null}
              </SelectContent>
            </Select>
            {!canInviteAdmin ? (
              <p className="text-muted-foreground text-xs">
                Only the workspace owner can invite admins.
              </p>
            ) : null}
          </div>

          {error ? (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={invite.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
